import {Button, Col, Flex, Input, Modal, Popover, Row, Space, Typography} from "antd";
import React, {useEffect} from "react";
import {Cover} from "../../components/Cover";
import '@mdxeditor/editor/style.css';
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles, diffSourcePlugin, DiffSourceToggleWrapper,
    ListsToggle,
    MDXEditor,
    toolbarPlugin
} from '@mdxeditor/editor';
import {headingsPlugin, listsPlugin} from '@mdxeditor/editor'
import {TagFlow} from "../../components/TagFlow";
import {Component} from "../../feature/project/setting";
import {Asset, Reference, Tag as TagField} from "../../feature/project/fields";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {
    addComponentAsset,
    addComponentReference,
    addComponentTag, removeComponentAsset,
    removeComponentReference, removeComponentTag
} from "../../feature/project/componentsSlice";
import {useParams} from "react-router-dom";
import {Status} from "../../feature/project/status";
import {fetchTags} from "../../feature/project/projectsSlice";
import {UploadGrid} from "../../components/UploadGrid";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBolt} from "@fortawesome/free-solid-svg-icons";
import {PersonNameGenerator} from "../../ai/tools/components/PersonNameGenerator";
import {PlaceNameGenerator} from "../../ai/tools/components/PlaceNameGenerator";
import {OtherNameGenerator} from "../../ai/tools/components/OtherNameGenerator";

export interface AddComponentProps {
    component: Component,
    // settingId: number,
    visible: boolean,
    onCancel: () => void,
    onSave: (component: Component) => void,
}

export const AddComponentModal = (props: AddComponentProps) => {
    const dispatch = useAppDispatch();
    const {projectId, storyId} = useParams();
    const allTags = useAppSelector(state => state.projects.tags)
    const status = useAppSelector(state => state.components.status)
    const [hasChanges, setHasChanges] = React.useState(false)
    const [title, setTitle] = React.useState(props.component.title)
    const [description, setDescription] = React.useState(props.component.description)
    const [cover, setCover] = React.useState(props.component.cover)
    const [assets, setAssets] = React.useState<Asset[]>(props.component.assets || [])
    const [references, setReferences] = React.useState<Reference[]>(props.component.references || [])
    const [tags, setTags] = React.useState<TagField[]>(props.component.tags || [])
    const [open, setOpen] = React.useState(false)

    useEffect(() => {
        setTitle(props.component.title)
        setDescription(props.component.description || props.component.category?.template || '')
        setCover(props.component.cover)
        setAssets(props.component.assets || [])
        setReferences(props.component.references || [])
        setTags(props.component.tags || [])
    }, [props.visible, props.component, props.component.category, props.component.assets, props.component.references, props.component.tags])

    useEffect(() => {
        setHasChanges(title !== props.component.title || description !== props.component.description || cover !== props.component.cover)
    }, [title, description, cover])

    useEffect(() => {
        if (status == Status.Idle && projectId && props.visible) {
            if (props.component.id) {
                dispatch(fetchTags({projectId: parseInt(projectId)}))
            }
        }
    }, [status, dispatch])

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
    };

    const onItemSelected = (item: any) => {
        let text = ""
        Object.keys(item).forEach((key, index) => {
            text = text + item[key] + (index < Object.keys(item).length - 1 ? ", " : '')
        })
        setTitle(text)
        setOpen(false)
    }
    return (
        <Modal
            maskClosable={false}
            open={props.visible}
            destroyOnClose={true}
            title={`${props.component.id ? 'Edit Component' : 'Add Component'} / ${props.component.category?.title}`}
            okText={'Save'}
            okButtonProps={{disabled: !hasChanges}}
            onCancel={() => props.onCancel()}
            onOk={() => props.onSave({
                id: props.component.id,
                title: title || "",
                description: description || "",
                category: props.component.category,
                cover: cover,
                assets: assets,
                references: references,
                tags: tags
            })
            }
            width={1000}
        >
            <Flex gap={16}>
                <Cover
                    projectId={projectId}
                    storyId={storyId}
                    style={{position: "relative", height: 400, width: 350, flex: 'none'}} cover={cover}
                    onFileUploaded={(url: string) => {
                        setCover(url)
                    }}/>
                <Space direction={'vertical'}
                       style={{width: '100%', maxHeight: 800, overflowY: 'auto', scrollbarWidth: "none"}} size={16}>
                    <div>
                        <Typography style={{fontWeight: 700}}>Title</Typography>
                        <Flex gap={8}><Input value={title} onChange={(e) => setTitle(e.target.value)}/>
                            <Popover
                                trigger={'click'}
                                placement="bottomRight"
                                title={"Generate"}
                                open={open}
                                onOpenChange={handleOpenChange}
                                content={
                                    props.component.category?.title == 'Character' ?
                                        <PersonNameGenerator
                                            onItemClick={onItemSelected}/> : props.component.category?.title == 'Location' ?
                                            <PlaceNameGenerator onItemClick={onItemSelected}/> :
                                            <OtherNameGenerator onItemClick={onItemSelected}/>
                                }>
                                <Button icon={<FontAwesomeIcon icon={faBolt}/>}/>
                            </Popover>
                        </Flex>
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Content</Typography>
                        <MDXEditor
                            className={"markdown-text"}
                            markdown={description || ""}
                            onChange={(e) => setDescription(e)}
                            plugins={[
                                headingsPlugin({allowedHeadingLevels: [1]}),
                                listsPlugin(),
                                toolbarPlugin({
                                    toolbarContents: () => (
                                        <DiffSourceToggleWrapper>
                                            <BlockTypeSelect/>
                                            <BoldItalicUnderlineToggles/>
                                            <ListsToggle options={['number', 'bullet']}/>
                                        </DiffSourceToggleWrapper>
                                    )
                                }),
                                diffSourcePlugin({ viewMode: 'rich-text' }),
                            ]}
                        />
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Assets</Typography>
                        <UploadGrid
                            projectId={projectId}
                            storyId={storyId}
                            files={assets.map(asset => asset.content || '')}
                            onUpload={(file) => {
                                setAssets([...assets, {content: file, type: 'image'}])
                                if (props.component.id && projectId) {
                                    dispatch(addComponentAsset({
                                        projectId: parseInt(projectId),
                                        componentId: props.component.id,
                                        asset: {content: file, type: 'image'}
                                    }))
                                }
                            }}
                            onDelete={(file) => {
                                setAssets(assets.filter((item) => item.content !== file))
                                const asset = assets.find((item) => item.content == file)
                                if (props.component.id && projectId && asset?.id) {
                                    dispatch(removeComponentAsset({
                                        projectId: parseInt(projectId),
                                        assetId: asset.id
                                    }))
                                }
                            }}
                        />
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <TagFlow allTags={allTags}
                                 tags={tags}
                                 onTagAdded={(tag) => {
                                     setTags([...tags, tag])
                                     if (props.component.id && projectId) {
                                         dispatch(addComponentTag({
                                             projectId: parseInt(projectId),
                                             componentId: props.component.id,
                                             tag: tag
                                         }))
                                     }
                                 }}
                                 onTagDeleted={(tag) => {
                                     if (tag.id && props.component.id && projectId) {
                                         dispatch(removeComponentTag({
                                             projectId: parseInt(projectId),
                                             componentId: props.component.id,
                                             tagId: tag.id
                                         }))
                                     }
                                 }}
                        />
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>References</Typography>
                        <UploadGrid
                            projectId={projectId}
                            storyId={storyId}
                            files={references.map(asset => asset.content || '')}
                            onUpload={(file) => {
                                setReferences([...references, {content: file, type: 'image'}])
                                if (props.component.id && projectId) {
                                    dispatch(addComponentReference({
                                        projectId: parseInt(projectId),
                                        componentId: props.component.id,
                                        reference: {content: file, type: 'image'}
                                    }))
                                }
                            }}
                            onDelete={(file) => {
                                setReferences(references.filter((item) => item.content !== file))
                                const reference = references.find((item) => item.content == file)
                                if (props.component.id && projectId && reference?.id) {
                                    dispatch(removeComponentReference({
                                        projectId: parseInt(projectId),
                                        referenceId: reference.id
                                    }))
                                }
                            }}
                        />
                    </div>
                </Space>
            </Flex>
        </Modal>
    )
        ;
}